import { ConsultantNode } from '../../types/career';
import { CanvasEdgeLayout, CanvasNodeLayout } from '../../types/canvas';
import { getNetRateDifference, getRateDifference } from '../../math/rates';
import { LayoutResult } from './RadialLayout';

const NODE_WIDTH = 96;
const NODE_HEIGHT = 96;
const GAP_X = 44;
const LEVEL_HEIGHT_V = 200; // 200px level drop in vertical tree
const LEVEL_WIDTH_H = 220; // 220px level distance in horizontal tree

interface SubtreeMetrics {
  span: number;
}

/**
 * Computes subtree span width/height bottom-up.
 */
function computeSubtreeMetrics(
  node: ConsultantNode,
  metricsMap: Map<string, SubtreeMetrics>,
  hideDormant: boolean,
  isVertical: boolean
): number {
  const visibleChildren = hideDormant ? node.children.filter((c) => c.isActive) : node.children;
  const nodeSize = isVertical ? NODE_WIDTH + GAP_X : NODE_HEIGHT + GAP_X + 16;

  if (visibleChildren.length === 0) {
    metricsMap.set(node.id, { span: nodeSize });
    return nodeSize;
  }

  let childrenSpanSum = 0;
  for (const child of visibleChildren) {
    childrenSpanSum += computeSubtreeMetrics(child, metricsMap, hideDormant, isVertical);
  }

  const span = Math.max(nodeSize, childrenSpanSum);
  metricsMap.set(node.id, { span });
  return span;
}

/**
 * Computes top-down (vertical) or left-to-right (horizontal) hierarchical tree layout.
 */
export function calculateTreeLayout(
  root: ConsultantNode,
  orientation: 'vertical' | 'horizontal' = 'vertical',
  hideDormant: boolean = false
): LayoutResult {
  const isVertical = orientation === 'vertical';
  const metricsMap = new Map<string, SubtreeMetrics>();
  computeSubtreeMetrics(root, metricsMap, hideDormant, isVertical);

  const nodes: CanvasNodeLayout[] = [];
  const edges: CanvasEdgeLayout[] = [];

  function layoutNode(
    node: ConsultantNode,
    parentId: string | null,
    depth: number,
    levelPos: number, // Y in vertical, X in horizontal
    spanStart: number, // start of allocated span
    spanEnd: number // end of allocated span
  ): { mainCoord: number; crossCoord: number } {
    const visibleChildren = hideDormant ? node.children.filter((c) => c.isActive) : node.children;

    let crossCoord = (spanStart + spanEnd) / 2;
    const mainCoord = levelPos;

    const childCoords: Array<{ id: string; x: number; y: number; level: number; monthlyAE: number }> = [];

    if (visibleChildren.length > 0) {
      let currentSpanStart = spanStart;

      for (const child of visibleChildren) {
        const childMetric = metricsMap.get(child.id)!;
        const childSpanEnd = currentSpanStart + childMetric.span;
        const nextLevelPos = levelPos + (isVertical ? LEVEL_HEIGHT_V : LEVEL_WIDTH_H);

        const coords = layoutNode(
          child,
          node.id,
          depth + 1,
          nextLevelPos,
          currentSpanStart,
          childSpanEnd
        );

        childCoords.push({
          id: child.id,
          x: isVertical ? coords.crossCoord : coords.mainCoord,
          y: isVertical ? coords.mainCoord : coords.crossCoord,
          level: child.level,
          monthlyAE: child.totalMonthlyAE,
        });

        currentSpanStart = childSpanEnd;
      }

      // Center parent exactly between first and last child
      if (childCoords.length > 0) {
        const firstChildCoord = isVertical ? childCoords[0].x : childCoords[0].y;
        const lastChildCoord = isVertical
          ? childCoords[childCoords.length - 1].x
          : childCoords[childCoords.length - 1].y;
        crossCoord = (firstChildCoord + lastChildCoord) / 2;
      }
    }

    const nodeX = isVertical ? crossCoord : mainCoord;
    const nodeY = isVertical ? mainCoord : crossCoord;

    nodes.push({
      id: node.id,
      data: node,
      x: Math.round(nodeX),
      y: Math.round(nodeY),
      depth,
      isRoot: parentId === null,
      parentId,
    });

    // Record edges to children
    for (const child of childCoords) {
      const rateDiff = getRateDifference(node.level, child.level as any);
      const netRateDiff = getNetRateDifference(node.level, child.level as any);
      const commission = child.monthlyAE * netRateDiff;

      edges.push({
        sourceId: node.id,
        targetId: child.id,
        sourceX: Math.round(nodeX),
        sourceY: Math.round(nodeY),
        targetX: Math.round(child.x),
        targetY: Math.round(child.y),
        mentorLevel: node.level,
        branchLevel: child.level as any,
        rateDifferenceUAH: rateDiff,
        netRateDifferenceUAH: netRateDiff,
        monthlyTurnoverAE: child.monthlyAE,
        lineCommissionUAH: commission,
        sourceIsRoot: parentId === null,
      });
    }

    return { mainCoord, crossCoord };
  }

  const rootSpan = metricsMap.get(root.id)!.span;
  layoutNode(root, null, 0, 0, -rootSpan / 2, rootSpan / 2);

  // Compute accurate bounding box enclosing all node cards and their badges/buttons
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const n of nodes) {
    minX = Math.min(minX, n.x - NODE_WIDTH / 2);
    maxX = Math.max(maxX, n.x + NODE_WIDTH / 2);
    minY = Math.min(minY, n.y - (n.isRoot ? 35 : 28));
    maxY = Math.max(maxY, n.y + 75);
  }

  if (nodes.length === 0) {
    minX = -NODE_WIDTH / 2;
    maxX = NODE_WIDTH / 2;
    minY = -NODE_HEIGHT / 2;
    maxY = NODE_HEIGHT / 2;
  }

  return {
    nodes,
    edges,
    bounds: { minX, maxX, minY, maxY },
  };
}
